import { DataTable } from '@pixel/core/DataTable';
import axios from 'axios';
import { DataTableColumnBase } from '@pixel/core/DataTable/DataTableTypes';
import { styled } from '@pixel/core/styles';
import ViewContextTableIcon from '@pixel/assets/icons/ViewContextTable';
import Typography, { TypographyProps } from '@pixel/core/Typography';
import React, { FC, useMemo, useState } from 'react';
import ListBaseSkeletonLoading from '../../components/ListBaseDefaultLoading';
import LocalizedTypography from '../../components/LocalizedTypography';
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes';
import useRunLogListQuery from '../hooks/useRunLogListQuery';
import { RunLogSearchResult, RunLogStatusType } from '../types/RunLog';
import { useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks';
import { ListBase } from '@pixel/core';
import moment from 'moment';
import { Link } from '@mui/material';
import { downloadFileFromUrl } from '../../utils/downloadFile';

interface RunLogSectionProps {
  companyAutomationId: string;
}

const runLogStatusTranslationMap = {
  STARTED: LocalizedText.RUN_LOG_STATUS_STARTED,
  COMPLETED: LocalizedText.RUN_LOG_STATUS_COMPLETED,
  COMPLETED_WITH_ERRORS: LocalizedText.RUN_LOG_STATUS_COMPLETED_WITH_ERRORS,
  FAILED: LocalizedText.RUN_LOG_STATUS_FAILED,
} as {
  [key in RunLogStatusType]: LocalizedText
}

const runLogStatusColorMap = {
  STARTED: '#8237e2',
  COMPLETED: '#47a84c',
  COMPLETED_WITH_ERRORS: '#f76700',
  FAILED: '#d32f2f'
} as {
  [key in RunLogStatusType]: TypographyProps['color']
}

const getFormattedDate = (date: Date | string): string => moment(date).format('YYYY/MM/DD hh:mm A Z');

const columns: Array<DataTableColumnBase<RunLogSearchResult>> = [
  {
    id: 'actionUserEId',
    columnName: window.i18n.automationEngine.RUN_LOG_HEADER_USER,
    minWidth: 0,
    renderCell: (node: RunLogSearchResult) => <Typography data-id={`runLog-${node.automationExecutionId}-actionUserEId`}>{node.actionUserEId}</Typography>
  },
  {
    id: 'status',
    columnName: window.i18n.automationEngine.RUN_LOG_HEADER_STATUS,
    minWidth: 0,
    renderCell: (node: RunLogSearchResult) => <LocalizedTypography data-id={`runLog-${node.automationExecutionId}-status`} color={runLogStatusColorMap[node.status]} text={runLogStatusTranslationMap[node.status]} />
  },
  {
    id: 'recordsAffected',
    columnName: window.i18n.automationEngine.RUN_LOG_HEADER_NR_AFFECTED,
    minWidth: 0,
    renderCell: (node: RunLogSearchResult) => <Typography data-id={`runLog-${node.automationExecutionId}-recordsAffected`}>{node.recordsAffected}</Typography>
  },
  {
    id: 'lastRanOn',
    columnName: window.i18n.automationEngine.RUN_LOG_HEADER_LAST_RUN,
    minWidth: 0,
    renderCell: (node: RunLogSearchResult) => <Typography data-id={`runLog-${node.automationExecutionId}-lastRanOn`}>{getFormattedDate(node.lastRanOn)}</Typography>
  }
];
const columnNames: Array<string> = columns.map(
  (column: DataTableColumnBase<RunLogSearchResult>) => column.columnName?.toUpperCase()
);
const TABLE_PER_PAGE_OPTIONS = [{ value: 10 }, { value: 25 }, { value: 50 }];

const RunLogSectionContainer = styled('div')`
  background-color: white;
  border-radius: 8px;
  margin-top: ${({ theme }: any) => theme.spacing(3)};
  margin-left: ${({ theme }: any) => theme.spacing(3)};
  margin-right: ${({ theme }: any) => theme.spacing(3)};
  padding: ${({ theme }: any) => theme.spacing(3)};
  
  .MuiToolbar-root, div[data-qa='ListBaseHeaderContainer'] {
    display: none;
  }
  
  .MuiTableContainer-root {
    margin-bottom: 0;
    box-shadow: none;

    tr:nth-child(odd) {
      height: 56px;
    }
    
    tr:nth-child(even):not(:last-of-type) td {
      border-bottom: 1px solid rgb(228, 237, 243);
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }

  .notes-about-this-run-label {
    color: #5b6971;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .description {
    max-width: 550px;
  }

  .download-link {
    margin-bottom: 16px;
  }

  .download-link, .expand-collapse-row-trigger {
    text-decoration: none;
    &:hover, &:focus {
      text-decoration: underline;
    }
  }
`;

const RunLogSection: FC<RunLogSectionProps> = ({ companyAutomationId }) => {
  const {
    RUN_LOG_ROWS_PER_PAGE,
    COMMON_VIEW_DATATABLE,
    RUN_LOG_DESCRIPTION_FOR_STARTED,
    RUN_LOG_DESCRIPTION_FOR_COMPLETED,
    RUN_LOG_DESCRIPTION_FOR_COMPLETED_WITH_ERRORS,
    RUN_LOG_DESCRIPTION_FOR_FAILED,
    RUN_LOG_DOWNLOAD_LABEL,
    RUN_LOG_EXPAND_ROW_LABEL,
    RUN_LOG_COLLAPSE_ROW_LABEL
  } = useMultipleLocalizedText();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(TABLE_PER_PAGE_OPTIONS[0].value);
  const { data, status } = useRunLogListQuery({
    companyAutomationId,
    page,
    count: perPage,
    sortOrder: "DESC",
    sortField: "ExecutionStartDate"
  });

  const downloadDetails = useMemo(() => async (runLog: RunLogSearchResult) => {
    const RUN_LOG_DOWNLOAD_ENDPOINT = '/admin/action/automation/admin/runLogsDetails/' + runLog.automationExecutionId;
    try {
      const response = await axios.get(RUN_LOG_DOWNLOAD_ENDPOINT);
      if ((response.status === 201 || response.status === 200) && response.data) {
        downloadFileFromUrl(response.data.RUN_LOG_CSV_URL);
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  const getRunLogDescription = useMemo(() => (runLog: RunLogSearchResult) => {
    if (runLog.status === 'STARTED') {
      return RUN_LOG_DESCRIPTION_FOR_STARTED
        .replace('{1}', runLog.automationName)
        .replace('{2}', getFormattedDate(runLog.lastRanOn));
    }
    if (runLog.status === 'COMPLETED') {
      return RUN_LOG_DESCRIPTION_FOR_COMPLETED
        .replace('{1}', runLog.automationName)
        .replace('{2}', getFormattedDate(runLog.lastRanOn))
        .replace('{3}', getFormattedDate(runLog.completedOn));
    }
    if (runLog.status === 'COMPLETED_WITH_ERRORS') {
      return RUN_LOG_DESCRIPTION_FOR_COMPLETED_WITH_ERRORS
        .replace('{1}', runLog.automationName)
        .replace('{2}', getFormattedDate(runLog.lastRanOn))
        .replace('{3}', getFormattedDate(runLog.completedOn))
        .replace('{4}', runLog.recordsProcessed + '')
        .replace('{5}', runLog.recordsAffected + '');
    }
    if (runLog.status === 'FAILED') {
      return RUN_LOG_DESCRIPTION_FOR_FAILED
        .replace('{1}', runLog.automationName)
        .replace('{2}', getFormattedDate(runLog.lastRanOn))
        .replace('{3}', getFormattedDate(runLog.completedOn));
    }
    return 'Unknown status for Run Log to get the description correctly.';
  }, []);

  return (
    <RunLogSectionContainer>
      <LocalizedTypography variant='h3' text={LocalizedText.AUTOMATION_RUN_LOG} />
      <ListBase
        showSearch={false}
        getRowID={(node: RunLogSearchResult) => node.automationExecutionId}
        pagingContext={{
          page: page,
          perPage: perPage,
          first: page * perPage + 1,
          last: Math.min(page * perPage + perPage, data?.totalCount ?? 0),
          hideFirstLastActions: true
        }}
        rowsPerPageLabel={RUN_LOG_ROWS_PER_PAGE}
        resultsPerPage={perPage}
        perPageOptions={TABLE_PER_PAGE_OPTIONS}
        onPageChanged={(e: any, nextPage: number) => setPage(nextPage)}
        onPaginationChange={(e: any) => {
          setPage(0);
          setPerPage(e.target.value);
        }}
        results={data || { nodes: [], totalCount: 0 }}
        resultsLoading={status === 'loading'}
        viewContexts={[
          {
            ariaLabel: COMMON_VIEW_DATATABLE,
            icon: <ViewContextTableIcon />,
            renderResults: (viewContextProps: any) => (
              <DataTable
                {...viewContextProps}
                renderExpand={(runLog: RunLogSearchResult) => (
                  <>
                    <LocalizedTypography className='notes-about-this-run-label' text={LocalizedText.RUN_LOG_NOTES_ABOUT_THIS_RUN} />
                    <Typography className='description'>{getRunLogDescription(runLog)}</Typography>
                    {runLog.status !== 'STARTED' ? <Link data-id={`runLog-${runLog.automationExecutionId}-downloadLink`} role='button' className='download-link' onClick={() => downloadDetails(runLog)}>{RUN_LOG_DOWNLOAD_LABEL}</Link> : null}
                  </>
                )}
                expandButtonPosition='right'
                renderExpandButton={({ isExpanded, handleCollapseClick }) => <Link role='button' className='expand-collapse-row-trigger' onClick={handleCollapseClick}>{isExpanded ? RUN_LOG_COLLAPSE_ROW_LABEL : RUN_LOG_EXPAND_ROW_LABEL}</Link>}
                labelExpandAriaLabel={RUN_LOG_EXPAND_ROW_LABEL}
                labelCollapseAriaLabel={RUN_LOG_COLLAPSE_ROW_LABEL}
                getRowID={(node: RunLogSearchResult) => node.automationExecutionId}
                columns={columns}
                results={data || { nodes: [], totalCount: 0 }}
              />
            ),
            renderLoading: () => <ListBaseSkeletonLoading columns={columnNames} items={5} />
          }
        ]}
        labelSearch=''
        labelSearchAria=''
        labelFilters=''
        labelClose=''
        labelApplyFilters=''
        labelClearAllFilters=''
        labelResults=''
        labelClear=''
      />
    </RunLogSectionContainer>
  );
};
export default RunLogSection;
